import { useEffect, useMemo, useRef, useState } from 'react'

import { DropdownProps } from './types'

// A form field's "nothing picked yet" default is usually `''`, not `null` — so
// an empty string must read as no selection. Otherwise the trigger renders
// `labelRenderer('')` (a raw key, a blank chip, whatever the consumer's mapping
// yields for an absent value) and offers a clear button on an untouched field.
export const hasDropdownValue = <T>(value: T | null | undefined): value is T =>
    value !== null && value !== undefined && (value as unknown) !== ''

type UseDropdownArgs<T> = Pick<
    DropdownProps<T>,
    'items' | 'value' | 'labelRenderer' | 'onChange' | 'onSearch' | 'createItem'
>

export function useDropdown<T>({
    items = [],
    value,
    labelRenderer,
    onChange,
    onSearch,
    createItem,
}: UseDropdownArgs<T>) {
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [triggerWidth, setTriggerWidth] = useState<number>()
    const [asyncItems, setAsyncItems] = useState<T[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)

    // Latest-callback ref: consumers may pass an inline `onSearch`, which must
    // not re-trigger the debounce — searches key on `query` only.
    const onSearchRef = useRef(onSearch)
    onSearchRef.current = onSearch
    const hasSearch = !!onSearch

    useEffect(() => {
        if (!hasSearch) {
            return
        }
        const trimmed = query.trim()
        if (!trimmed) {
            setAsyncItems([])
            setIsLoading(false)
            setIsError(false)
            return
        }
        setIsLoading(true)
        setIsError(false)
        let cancelled = false
        const timer = setTimeout(() => {
            onSearchRef
                .current?.(trimmed)
                .then(results => {
                    if (!cancelled) {
                        setAsyncItems(results)
                        setIsLoading(false)
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setAsyncItems([])
                        setIsError(true)
                        setIsLoading(false)
                    }
                })
        }, 300)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [query, hasSearch])

    const filtered = useMemo(() => {
        if (onSearch) {
            return asyncItems
        }
        const trimmed = query.trim()
        const q = trimmed.toLowerCase()
        const matches = q
            ? items.filter(item => labelRenderer(item).toLowerCase().includes(q))
            : items
        return createItem && trimmed && !matches.some(item => labelRenderer(item) === trimmed)
            ? [...matches, createItem(trimmed)]
            : matches
    }, [onSearch, asyncItems, query, items, labelRenderer, createItem])

    const selectedLabel = hasDropdownValue(value) ? labelRenderer(value) : undefined

    const discard = () => {
        setIsOpen(false)
        setQuery('')
    }

    const close = () => {
        const trimmed = query.trim()
        if (createItem && trimmed && trimmed !== selectedLabel) {
            onChange(items.find(item => labelRenderer(item) === trimmed) ?? createItem(trimmed))
        }
        discard()
    }

    const toggle = () => {
        if (isOpen) {
            close()
        } else {
            setIsOpen(true)
        }
    }

    const select = (item: T) => {
        onChange(item)
        discard()
    }

    const clear = () => {
        onChange(null)
        discard()
    }

    const fieldValue = isOpen ? query : (selectedLabel ?? '')

    return {
        isOpen,
        setIsOpen,
        setQuery,
        triggerWidth,
        setTriggerWidth,
        filtered,
        selectedLabel,
        fieldValue,
        isLoading,
        isError,
        close,
        toggle,
        select,
        clear,
    }
}
